import { useState } from "react";
import { now } from "@freightsimple/time/helpers";
import { usePingApi } from "@/Hooks/usePingApi";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { getEnvironment } from "@/Environment";
import { useIsAuthenticated } from "@/Hooks/useIsAuthenticated";
import {
  buildInitialTests,
  formatResultsForCopy,
} from "../Helpers/connectivityTestTypes";
import type { ConnectivityTestResults } from "../Helpers/connectivityTestTypes";
import {
  testApiConnectivity,
  testCoreEndpoints,
  testDomainResponseTime,
  testFreightSimpleInfrastructure,
  testStaticAssets,
} from "../Helpers/connectivityTestServerChecks";
import {
  testBrowserEnvironment,
  testDnsResolution,
  testExternalServices,
  testNetworkPathAnalysis,
  testRegionalConnectivity,
} from "../Helpers/connectivityTestNetworkChecks";

export interface UseConnectivityTestsResult {
  results: ConnectivityTestResults | undefined;
  isRunning: boolean;
  isCopied: boolean;
  runAllTests: () => Promise<void>;
  copyResults: () => Promise<void>;
}

export function useConnectivityTests(): UseConnectivityTestsResult {
  const [results, setResults] = useState<ConnectivityTestResults | undefined>(
    undefined,
  );
  const [isRunning, setIsRunning] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const pingApi = usePingApi();
  const shipmentsApi = useShipmentsApi();
  const paymentMethodsApi = usePaymentMethodsApi();
  const isAuthenticated = useIsAuthenticated();

  function updateTest(
    index: number,
    updates: Partial<ConnectivityTestResults["tests"][number]>,
  ) {
    setResults((prev) => {
      if (!prev) return prev;
      const tests = [...prev.tests];
      const existing = tests[index];
      if (existing === undefined) return prev;
      tests[index] = { ...existing, ...updates };
      return { ...prev, tests };
    });
  }

  async function runAllTests() {
    setIsRunning(true);
    setResults({
      timestamp: now(),
      browser: navigator.userAgent,
      environment: getEnvironment(),
      tests: buildInitialTests(isAuthenticated),
      overallStatus: "ok",
    });

    let index = 0;
    await testDomainResponseTime(index++, updateTest);
    await testApiConnectivity(index++, updateTest, () => pingApi.ping());

    if (isAuthenticated) {
      await testCoreEndpoints(
        index++,
        updateTest,
        () => shipmentsApi.getShipments(),
        () => paymentMethodsApi.getAllPaymentMethods(),
      );
    }

    await testExternalServices(index++, updateTest);
    await testRegionalConnectivity(index++, updateTest);
    await testDnsResolution(index++, updateTest);
    await testFreightSimpleInfrastructure(index++, updateTest);
    await testNetworkPathAnalysis(index++, updateTest);
    await testStaticAssets(index++, updateTest);
    await testBrowserEnvironment(index++, updateTest);

    // Let the final state updates flush before deriving the overall verdict.
    setTimeout(() => {
      setResults((prev) => {
        if (!prev) return prev;
        const failed = prev.tests.filter((t) => t.status === "failed").length;
        const warnings = prev.tests.filter(
          (t) => t.status === "warning",
        ).length;
        const overallStatus =
          failed > 0 ? "critical" : warnings > 0 ? "issues" : "ok";
        return { ...prev, overallStatus };
      });
    }, 100);

    setIsRunning(false);
  }

  async function copyResults() {
    if (!results) return;
    try {
      await navigator.clipboard.writeText(formatResultsForCopy(results));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy results:", error);
    }
  }

  return { results, isRunning, isCopied, runAllTests, copyResults };
}
